import { type PointerEvent, useCallback, useRef } from 'react';

interface Options {
  onTap?: (event: PointerEvent | TouchEvent | MouseEvent) => void;
  onSwipeDown?: (
    event: PointerEvent | TouchEvent | MouseEvent,
    info: { distanceY: number; velocityY: number; durationMs: number },
  ) => void;
  // Tuning thresholds
  tapMaxDurationMs?: number; // maximum duration to still be considered a tap
  tapMaxMovementPx?: number; // maximum movement to still be considered a tap
  swipeDownMinDistancePx?: number; // minimum downward distance
  swipeDownMinVelocityPxPerMs?: number; // minimum velocity
  doubleTapDelayMs?: number; // time window to treat as double-tap (ignored)
}

interface BindHandlers {
  onPointerDown: (e: PointerEvent) => void;
  onPointerMove: (e: PointerEvent) => void;
  onPointerUp: (e: PointerEvent) => void;
  onPointerCancel: (e: PointerEvent) => void;
}

/**
 * useTapOrSwipeDown
 *
 * Detects a single tap or a swipe-down gesture while ignoring double-tap and pinch gestures.
 *
 * Notes:
 * - Uses Pointer Events, so it works for mouse, touch, and pen.
 * - Pinch is ignored by detecting when multiple pointers are active at once.
 * - Double tap is ignored by timing of recent successful taps.
 * - Provide handlers for onTap and onSwipeDown. Attach returned bind handlers to the target element.
 */
export default function useTapOrSwipeDown(options: Options = {}): BindHandlers {
  const {
    onTap,
    onSwipeDown,
    tapMaxDurationMs = 300,
    tapMaxMovementPx = 10,
    swipeDownMinDistancePx = 40,
    swipeDownMinVelocityPxPerMs = 0.2, // ~200px in 1s or faster
    doubleTapDelayMs = 300,
  } = options;

  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const lastXRef = useRef(0);
  const lastYRef = useRef(0);
  const startTimeRef = useRef(0);
  const multiPointerActiveRef = useRef(false);
  const activePointersRef = useRef(new Set<number>());
  const lastTapTimeRef = useRef(0);
  const pendingTapTimeoutRef = useRef<number | null>(null);
  const gestureActive = useRef(false);

  const clearPendingTap = () => {
    if (pendingTapTimeoutRef.current !== null) {
      clearTimeout(pendingTapTimeoutRef.current);
      pendingTapTimeoutRef.current = null;
    }
  };

  const resetGesture = () => {
    gestureActive.current = false;
    startXRef.current = 0;
    startYRef.current = 0;
    lastXRef.current = 0;
    lastYRef.current = 0;
    startTimeRef.current = 0;
    activePointersRef.current.clear();
    multiPointerActiveRef.current = false;
  };

  const onPointerDown = useCallback((e: PointerEvent) => {
    // Track pointers to detect pinch/multi-touch
    activePointersRef.current.add(e.pointerId);
    if (activePointersRef.current.size > 1) {
      multiPointerActiveRef.current = true; // pinch or multi-pointer: ignore this gesture
    }

    if (!gestureActive.current) {
      gestureActive.current = true;
      startXRef.current = e.clientX;
      startYRef.current = e.clientY;
      lastXRef.current = e.clientX;
      lastYRef.current = e.clientY;
      startTimeRef.current = e.timeStamp;
    }
  }, []);

  const onPointerMove = useCallback((e: PointerEvent) => {
    if (!gestureActive.current) return;
    lastXRef.current = e.clientX;
    lastYRef.current = e.clientY;
  }, []);

  const finish = useCallback(
    (e: PointerEvent) => {
      // Remove pointer from active set
      activePointersRef.current.delete(e.pointerId);

      if (!gestureActive.current) return resetGesture();

      const duration = e.timeStamp - startTimeRef.current;
      const dx = lastXRef.current - startXRef.current;
      const dy = lastYRef.current - startYRef.current;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      const distance = Math.hypot(dx, dy);
      const velocityY = duration > 0 ? dy / duration : 0;

      // Ignore if any multi-pointer interaction occurred (pinch/zoom)
      if (multiPointerActiveRef.current) {
        resetGesture();
        return;
      }

      // Swipe down detection: mainly downward, enough distance and velocity
      const isSwipeDown =
        dy > 0 &&
        absDy > absDx &&
        absDy >= swipeDownMinDistancePx &&
        Math.abs(velocityY) >= swipeDownMinVelocityPxPerMs;

      // Tap detection: short duration and minimal movement
      const isTap = duration <= tapMaxDurationMs && distance <= tapMaxMovementPx;

      if (isSwipeDown) {
        onSwipeDown?.(e.nativeEvent, { distanceY: dy, velocityY, durationMs: duration });
        resetGesture();
        return;
      }

      if (isTap) {
        // Double-tap ignore window: delay emitting single tap until the window passes
        const now = e.timeStamp;
        if (now - lastTapTimeRef.current <= doubleTapDelayMs) {
          // Second tap within window => cancel pending single-tap and ignore
          lastTapTimeRef.current = 0;
          clearPendingTap();
        } else {
          lastTapTimeRef.current = now;
          const native = e.nativeEvent; // capture before timeout to avoid synthetic event pooling issues
          clearPendingTap();
          pendingTapTimeoutRef.current = window.setTimeout(() => {
            pendingTapTimeoutRef.current = null;
            lastTapTimeRef.current = 0;
            onTap?.(native);
          }, doubleTapDelayMs);
        }
      }

      resetGesture();
    },
    [
      doubleTapDelayMs,
      onSwipeDown,
      onTap,
      swipeDownMinDistancePx,
      swipeDownMinVelocityPxPerMs,
      tapMaxDurationMs,
      tapMaxMovementPx,
    ],
  );

  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      finish(e);
    },
    [finish],
  );

  const onPointerCancel = useCallback((e: PointerEvent) => {
    // Treat cancel as end without triggering actions
    activePointersRef.current.delete(e.pointerId);
    resetGesture();
  }, []);

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
